import React, { type ReactElement } from 'react';
import { Currency } from 'src/components/currency';
import { COUNTRY_NAME_BY_CURRENCY_MAP } from 'src/constants/country-name-by-currency-map';
import { MIN_LTF_PRICE } from 'src/constants/learn-more';
import { type CurrencyType } from 'src/types/currency';
import {
  type InstallmentOffer,
  type ServiceFeePolicy,
  type WidgetOfferPeriod,
} from 'src/types/widget-offers';
import { getFormattedAmount } from 'src/utils/get-formatted-amount';

const PROCESSING_FEE_PERCENTAGE = 1.15;
const FEE_CAP = 57.5;

const getAdjustedFee = ({
  fullAmount,
  totalFees,
}: {
  fullAmount: number;
  totalFees: number;
}) => {
  const fees = Math.round(fullAmount * PROCESSING_FEE_PERCENTAGE) / 100;

  if (totalFees > fees) {
    return fees > FEE_CAP ? FEE_CAP : fees;
  }

  return totalFees > FEE_CAP ? FEE_CAP : totalFees;
};

const shouldShowProcessingFeeDescription = (
  currency: CurrencyType
): boolean => {
  const country = COUNTRY_NAME_BY_CURRENCY_MAP[currency];
  return country === 'saudi';
};

type Props = {
  currency: CurrencyType;
  serviceFeePolicy?: ServiceFeePolicy;
  totalFees: number;
  fullAmount: number;
  feesAmount: string | null;
  paymentsCount: number;
  period: WidgetOfferPeriod;
  t: (
    value: string,
    options?: Record<string, unknown>,
    сomponetsMap?: Record<string, (content?: string) => ReactElement>
  ) => string;
};

export function getFeesDescription({
  serviceFeePolicy,
  totalFees,
  feesAmount,
  currency,
  fullAmount,
  paymentsCount,
  period,
  t,
}: Props) {
  const country = COUNTRY_NAME_BY_CURRENCY_MAP[currency];
  const isPi4 = paymentsCount === 4;

  if (!feesAmount) {
    return t('paid_plans.no_interest_no_fees');
  }

  const fees =
    country === 'saudi' && isPi4
      ? getAdjustedFee({ fullAmount, totalFees })
      : totalFees;

  switch (serviceFeePolicy) {
    case 'overall': {
      const feePerPayment = Math.floor((fees / paymentsCount) * 100) / 100;
      const formattedFeeAmount = getFormattedAmount(feePerPayment, currency);
      if (
        shouldShowProcessingFeeDescription(currency) &&
        period.size === 'month'
      ) {
        return t(
          'monthly_processing_fee_included',
          {
            serviceFee: formattedFeeAmount,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      }

      if (period.size === 'month') {
        return t(
          'includes_amount_montlhy_fee',
          {
            serviceFee: formattedFeeAmount,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      }

      if (period.size === 'week' && period.value === 1) {
        return t(
          'includes_amount_weekly_fee',
          {
            serviceFee: formattedFeeAmount,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      }

      if (period.size === 'week' && period.value === 2) {
        return t(
          'includes_amount_bi_weekly_fee',
          {
            serviceFee: formattedFeeAmount,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      }

      return t(
        'paid_plans.service_fee_in_total',
        {
          serviceFee: feesAmount,
        },
        {
          currency: () => <Currency currency={currency} />,
        }
      );
    }
    case 'annuity': {
      return t(
        'paid_plans.average_monthly_profit',
        {
          serviceFee: (totalFees / paymentsCount).toFixed(2),
          minPrice: getFormattedAmount(MIN_LTF_PRICE, currency),
        },
        {
          currency: () => <Currency currency={currency} />,
        }
      );
    }
    case 'downpayment_only': {
      const feeValue = Number(fees.toFixed(2));
      const formattedFeeAmount = getFormattedAmount(feeValue, currency);

      if (shouldShowProcessingFeeDescription(currency)) {
        return t(
          'one_time_processing_fee',
          {
            serviceFee: formattedFeeAmount,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      }

      return t(
        'plus_one_time_upfront_service_fee',
        {
          serviceFee: feesAmount,
        },
        {
          currency: () => <Currency currency={currency} />,
        }
      );
    }
    case 'per_installment':
      return t(
        'paid_plans.service_fee_in_total',
        {
          serviceFee: feesAmount,
        },
        {
          currency: () => <Currency currency={currency} />,
        }
      );
    default:
      if (country === 'saudi' && isPi4) {
        return t('no_interest_processing_fee_may_apply');
      }

      return t('paid_plans.no_interest_no_fees');
  }
}

export const getSortedInstallmentsOffers = (
  installments: readonly InstallmentOffer[]
) =>
  installments
    .map((installment, index) => ({ index, installment }))
    .sort(
      (a, b) =>
        Number(Boolean(b.installment.subventionCampaignId)) -
          Number(Boolean(a.installment.subventionCampaignId)) ||
        // Preserve production's reversed campaign group without mutating input.
        (a.installment.subventionCampaignId
          ? b.index - a.index
          : a.index - b.index)
    )
    .map(({ installment }) => installment);

const SERVICE_FEE_PERCENT_NO_INTEREST = 1.16;

type IsShowNoInterestOptions = {
  currency: CurrencyType;
  serviceFeePercent?: string;
  serviceFeePolicy?: ServiceFeePolicy;
};
export const getIsShowNoInterest = (options: IsShowNoInterestOptions) => {
  const { currency, serviceFeePercent, serviceFeePolicy } = options;
  const country = COUNTRY_NAME_BY_CURRENCY_MAP[currency];

  return Boolean(
    country === 'saudi' &&
      serviceFeePercent &&
      Number(serviceFeePercent) < SERVICE_FEE_PERCENT_NO_INTEREST &&
      Number(serviceFeePercent) > 0 &&
      serviceFeePolicy === 'overall'
  );
};

type IsLimitedRamadanOfferOptions = {
  currency: CurrencyType;
  serviceFeePercent?: string;
  serviceFeePolicy?: ServiceFeePolicy;
  paymentsCount: number;
};
// if country is SAR AND payments count > 4 AND service_fee_policy is NOT annuity
export const getIsLimitedRamadanOffer = (
  options: IsLimitedRamadanOfferOptions
) => {
  const { currency, serviceFeePercent, serviceFeePolicy, paymentsCount } =
    options;
  const country = COUNTRY_NAME_BY_CURRENCY_MAP[currency];

  return Boolean(
    country === 'saudi' &&
      Number(serviceFeePercent) < SERVICE_FEE_PERCENT_NO_INTEREST &&
      serviceFeePolicy === 'overall' &&
      paymentsCount > 4
  );
};

export const filterInstallmentsOffers = (
  installments: InstallmentOffer[],
  hideLftSarExperiment: boolean,
  currency: CurrencyType
) => {
  const country = COUNTRY_NAME_BY_CURRENCY_MAP[currency];

  if (!hideLftSarExperiment || country !== 'saudi') {
    return installments;
  }

  return installments.filter((option) => option.serviceFeePolicy !== 'annuity');
};
